import React, { useState } from 'react';
import DSBox from '../../../../design-system/DSBox';
import DSLabelTypography from '../../../../design-system/Typography/DSLabelTypography';
import DSIconButton from '../../../../design-system/DSIconButton';
import DSHelpOutline from '../../../../design-system/DSHelpOutline';
import DSTooltip from '../../../../design-system/DSTooltip';
import { useTheme } from '@mui/material';
import { PopulatedKlydo } from '../../../../api/klydo';
import DSAvatar from '../../../../design-system/DSAvatar';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SelectItemModal from './SelectItemModal';
import KlydoCard from '../../../Klydos/KlydosList/components/KlydoCard';
import {
  COLLECTION_THUMBNAIL_KLYDOS_SPEC,
  SELECT_6_KLYDOS_SPEC_IMAGE,
} from '../../../../consts/specs';

type CollectionDisplayKlydosSelectorProps = {
  collectionKlydos: PopulatedKlydo[];
  selectedKlydoIds: string[];
  onSelectedKlydosChange: (klydoIds: string[]) => void;
};

const CollectionDisplayKlydosSelector = ({
  collectionKlydos,
  selectedKlydoIds,
  onSelectedKlydosChange,
}: CollectionDisplayKlydosSelectorProps) => {
  const { spacing, palette } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedKlydos = collectionKlydos.filter((klydo) =>
    selectedKlydoIds.includes(klydo.id),
  );

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmSelection = (klydoIds: string[]) => {
    onSelectedKlydosChange(klydoIds);
  };

  const renderKlydoItem = (
    klydo: PopulatedKlydo,
    isSelected: boolean,
    onToggle: () => void,
  ) => (
    <KlydoCard
      klydo={klydo}
      isSelected={isSelected}
      onSelect={() => onToggle()}
      isStatic
      isMenuHidden
    />
  );

  return (
    <>
      <DSBox
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing(1),
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing(0.5),
          }}
        >
          <DSLabelTypography>Thumbnail Klydos (6 required)</DSLabelTypography>
          <DSTooltip
            title={
              <DSBox
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <span>{COLLECTION_THUMBNAIL_KLYDOS_SPEC}</span>
                <img
                  src={SELECT_6_KLYDOS_SPEC_IMAGE}
                  alt="Select 6 Klydos specification"
                  style={{ maxWidth: '300px', borderRadius: '8px' }}
                />
              </DSBox>
            }
          >
            <DSIconButton
              sx={{
                height: spacing(2),
                width: spacing(2),
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: palette.text.primary,
                },
                color: palette.text.secondary,
                transition: 'color 0.3s ease',
              }}
            >
              <DSHelpOutline sx={{ fontSize: 16 }} />
            </DSIconButton>
          </DSTooltip>
        </DSBox>

        {selectedKlydos.length > 0 ? (
          <DSBox
            onClick={handleOpenModal}
            sx={{
              display: 'flex',
              height: '74px',
              padding: '16px 20px',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderRadius: '16px',
              backgroundColor: palette.paper.elevation3,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: palette.paper.elevation6,
              },
            }}
          >
            <DSBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing(1),
                flex: 1,
              }}
            >
              {selectedKlydos.slice(0, 6).map((klydo) => (
                <DSAvatar
                  key={klydo.id}
                  src={klydo.photoUrl}
                  alt={klydo.name}
                  sx={{
                    width: '42px',
                    height: '42px',
                    border: `2px solid ${palette.common.black}`,
                  }}
                />
              ))}
              <DSLabelTypography sx={{ marginLeft: spacing(1) }}>
                {selectedKlydos.length} Selected
              </DSLabelTypography>
            </DSBox>
            <ArrowDropDownIcon sx={{ color: palette.text.secondary }} />
          </DSBox>
        ) : (
          <DSBox
            onClick={handleOpenModal}
            sx={{
              display: 'flex',
              height: '74px',
              padding: '16px 32px',
              justifyContent: 'space-between',
              alignItems: 'center',
              alignSelf: 'stretch',
              borderRadius: '16px',
              backgroundColor: palette.paper.elevation3,
              cursor: 'pointer',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: palette.paper.elevation6,
              },
            }}
          >
            <DSLabelTypography color="textSecondary">
              Select your chosen 6 Klydos
            </DSLabelTypography>
            <ArrowDropDownIcon sx={{ color: palette.text.secondary }} />
          </DSBox>
        )}
      </DSBox>

      <SelectItemModal<PopulatedKlydo>
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title="Select 6 Chosen Klydos"
        subtitle="Click to choose the Klydos you want to add to your Collection"
        items={collectionKlydos}
        getItemId={(klydo) => klydo.id}
        selectedItemIds={selectedKlydoIds}
        onConfirm={handleConfirmSelection}
        maxSelection={6}
        renderItem={renderKlydoItem}
        gridColumns={3}
      />
    </>
  );
};

export default CollectionDisplayKlydosSelector;
